import { Field } from "../../../../types/TenantInterfaces";
import { LinkSettings } from "../ReusableViewSettings";

type ValidSources =
  | {
      FieldName: string;
      FieldAPIName: string;
      ModuleAPINames: string[];
      IsSubitem: false;
    }
  | {
      FieldName: string;
      FieldAPIName: string;
      IsSubitem: true;
      Fields: {
        FieldName: string;
        FieldAPIName: string;
        ModuleAPINames: string[];
      }[];
    };

export function FormatReferenceViewFields({
  referenceFields,
  globalAppViews,
  viewID,
}: {
  referenceFields: Field[];
  viewID: string;
  globalAppViews: any[];
}) {
  const linkSettings: LinkSettings[] = [];

  /**
   * An array to store all valid sources that will be shown in the Link Settings option.
   *
   * Invalid sources (will be skipped):
   * - SourceList is equal to "Any"
   * - Subitem references.
   * - Array references which contains more than 5 sources in it.
   */
  const validSources = GetSourceNames(referenceFields);

  if (!validSources?.length) return [];

  for (const validSource of validSources) {
    let object: any = {};

    if (validSource.IsSubitem) {
      object = {
        Fields: validSource.Fields.map((f) => {
          return {
            Name: f.FieldName,
            APIName: f.FieldAPIName,
            Type: "Field",
            ModuleViews: f.ModuleAPINames.map((mApiName) => ({
              ModuleAPIName: mApiName,
              Views: globalAppViews.filter(
                (v: any) => v._id !== viewID && v?.ModuleAPIName === mApiName
              ),
            })),
          };
        }),
      };
    } else {
      object = {
        ModuleViews: validSource.ModuleAPINames.map((mApiName) => ({
          ModuleAPIName: mApiName,
          Views: globalAppViews.filter(
            (v: any) => v._id !== viewID && v?.ModuleAPIName === mApiName
          ),
        })),
      };
    }

    linkSettings.push({
      Name: validSource.FieldName,
      APIName: validSource.FieldAPIName,
      Type: "Field",
      IsSubitem: validSource.IsSubitem,
      ...object,
    });
  }

  return linkSettings;
}

/**
 * Returns the Module APIName from Reference -> SourcesList Fields.
 */
function GetSourceName(source: string) {
  if (source === "Any") return null;

  const moduleAPIName = source.replace("Tenant.Modules.", "");

  // It's a Subitem reference.
  if (moduleAPIName.includes(".")) return null;

  return moduleAPIName;
}

/**
 * Recursive function which gets all the module APINames from the sourcesList of
 * Reference fields, including Subitem Fields.
 */
function GetSourceNames(referenceFields: Field[]) {
  let validSources: ValidSources[] = [];

  for (const referenceField of referenceFields) {
    switch (referenceField.FieldType) {
      case "Subitem":
      case "MultiSubitem":
        if (
          !referenceField?.SubitemFields?.length ||
          referenceField?.APIName === "_RemoteSystems"
        )
          continue;

        let nestedValidSources = GetSourceNames(referenceField?.SubitemFields);

        if (!nestedValidSources?.length) break;

        validSources.push({
          FieldName: referenceField.Name,
          FieldAPIName: referenceField.APIName,
          IsSubitem: true,
          // @ts-ignore
          Fields: nestedValidSources,
        });
        break;

      case "SingleReference":
      case "MultiReference":
        const sourcesList = referenceField?.SourcesList;
        if (!sourcesList) continue;

        let fieldName = referenceField.Name;

        let fieldAPIName = referenceField?.SubitemParent
          ? `${referenceField?.SubitemParent}.${referenceField.APIName}`
          : referenceField.APIName;

        if (Array.isArray(sourcesList)) {
          if (sourcesList?.length > 5) continue;
          let validModuleAPINames = [];

          for (let source of sourcesList) {
            let moduleAPIName = GetSourceName(source);

            if (!moduleAPIName) continue;

            validModuleAPINames.push(moduleAPIName);
          }

          if (validModuleAPINames.length) {
            validSources.push({
              ModuleAPINames: validModuleAPINames,
              FieldName: fieldName,
              FieldAPIName: fieldAPIName,
              IsSubitem: false,
            });
          }
        } else {
          // It's a string.
          sourcesList;
          let moduleAPIName = GetSourceName(sourcesList);

          if (!moduleAPIName) continue;

          validSources.push({
            ModuleAPINames: [moduleAPIName],
            FieldName: fieldName,
            FieldAPIName: fieldAPIName,
            IsSubitem: false,
          });
        }

        break;
    }
  }

  return validSources;
}
